import { fsSession } from '../constants/full-story-session';
import { PendoAccount } from '../interfaces/pendo-account.interface';
import { PendoData } from '../interfaces/pendo-data.interface';
import { PendoVisitor } from '../interfaces/pendo-visitor.interface';
import { IndicatorMetrics } from '../interfaces/product-indicators.interface';
import { AgencyModel } from '../models/agency.model';
import { UserModel } from '../models/user.model';

import dayjs from 'dayjs';
import { ProductType } from '../type-aliases/product-type';

export class PendoModel {
  public indicatorMetrics: IndicatorMetrics = {
    has_first_call: false,
    has_first_notification: false,
    no_forms_product: false,
    no_ci_product: false,
  };

  private agency: AgencyModel;
  private user: UserModel;

  constructor(agency: AgencyModel, user: UserModel, metrics) {
    this.user = user;
    this.agency = agency;
    this.indicatorMetrics = { ...this.indicatorMetrics, ...metrics };
  }

  public get payload(): PendoData {
    return {
      visitor: this.userData,
      account: this.agencyData,
    };
  }

  private get agencyData(): PendoAccount {
    if (this.agency) {
      const [
        id,
        name,
        activeTrackerCount,
        state,
        callsEnabled,
        formsEnabled,
        leadWithLeadCenterEnabled,
        activeCompanyCount,
      ] = this.hipaaSensitiveValue(this.agency, [
        'id',
        'name',
        'active_tracker_count',
        'state',
        'calls_enabled',
        'forms_enabled',
        'lead_with_lead_center',
        'active_company_count',
      ]);

      const leadCenterPlanName = this.indicatorMetrics.lead_center_plan_name;
      const leadWithLeadCenterPlanName =
        this.indicatorMetrics.lead_with_lead_center_plan_name;
      const accountData: PendoAccount = {
        id,
        name,
        activeTrackerCount,
        state,
        callsEnabled,
        formsEnabled,
        leadWithLeadCenterEnabled,
        leadCenterPlanName,
        leadWithLeadCenterPlanName,
        industry: this.agency.industry,
        chatsEnabled: this.agency.hasFeature('lc_chat'),
        creationDate: this.isoDateString(this.agency.created_at),
        hasFirstCall: this.indicatorMetrics.has_first_call,
        hasFirstRecordedCall: this.indicatorMetrics.has_first_recorded_call,
        hasFirstTranscribedCall:
          this.indicatorMetrics.has_first_transcribed_call,
        hasFirstForm: this.indicatorMetrics.has_first_form,
        hasSoftphoneMins: this.indicatorMetrics.has_softphone_mins,
        formsTrial: this.indicatorMetrics.forms_trial,
        callsTrial: this.indicatorMetrics.calls_trial,
        lcTrialDays: this.indicatorMetrics.lc_trial,
        ciTrial: this.indicatorMetrics.ci_trial,
        callRecording: this.indicatorMetrics.call_recording,
        callScribeEnabled: this.agency.hasFeature('call_scribe'),
        noFormsProduct: this.indicatorMetrics.no_forms_product,
        noCiProduct: this.indicatorMetrics.no_ci_product,
        formsPlanName: this.indicatorMetrics.forms_plan_name,
        callsPlanName: this.indicatorMetrics.calls_plan_name,
        features: this.agency.features,
        jsNeverAccessed: this.indicatorMetrics.js_never_accessed,
        jsActive: this.indicatorMetrics.js_active,
        whiteLabelEnabled: this.agency.white_label,
        leadCenterEnabled:
          !!leadCenterPlanName && this.agency.hasFeature('contact_center'),
        agentsCount: this.indicatorMetrics.agents_count,
        usersCount: this.indicatorMetrics.users_count,
        hasLctexts: this.indicatorMetrics.has_first_sms,
        hasWebsitePool: this.indicatorMetrics.has_website_pool,
        hasSummaries: this.agency.hasFeature('call_summaries'),
        ciPlanName: this.getPlanName('conversation_intelligence'),
        brandStatus: this.agency.brand_status,
        hasTwoPlusCompany: activeCompanyCount >= 2,
        accountOwnerName:
          this.indicatorMetrics.salesforce_account_owner_name || 'N/A',
        ciActiveDays: this.indicatorMetrics.ci_active_days,
        caTrialDays: this.indicatorMetrics.ca_trial_days,
        caEnabled: this.agency.hasFeature('ai_convert_assist'),
        kycNeeded: this.indicatorMetrics.kyc_needed || false,
        caPlanName: this.getPlanName('convert_assist'),
        businessProfileComplete:
          this.indicatorMetrics.business_profile_complete || false,
        voiceAssistPersonalized:
          this.indicatorMetrics.voice_assist_personalized || false,
        voiceAssistLiveInCallFlow:
          this.indicatorMetrics.voice_assist_live_in_call_flow || false,
        voiceAssistNotificationsEnabled:
          this.indicatorMetrics.voice_assist_notifications_enabled || false,
        firstVoiceAssistCall:
          this.indicatorMetrics.first_voice_assist_call || false,
        vaTrialDays: this.indicatorMetrics.va_trial_days,
        vaRemovedDays: this.indicatorMetrics.va_removed_days,
        vaRemovedAt: this.indicatorMetrics.va_removed_at,
        vaEnabled: this.platforms.includes('voice_assist'),
        vaPlanName: this.getPlanName('voice_assist'),
        // VA-First cohort, so Pendo can exclude them from guides like the
        // call recording notice.
        vaFirst: !!this.agency.va_without_calls,
        resellerPartner: this.agency.reseller_partner,
        hasManualAutomationRule:
          this.indicatorMetrics.has_manual_automation_rule || false,
      };
      return accountData;
    }
  }

  private get userData(): PendoVisitor {
    const [id, hipaa, email, role, mcc] = this.hipaaSensitiveValue(this.user, [
      'id',
      'hipaa',
      'email',
      'role',
      'mcc',
    ]);
    const userData: PendoVisitor = {
      id,
      hipaa,
      email,
      role,
      mcc,
      creationDate: this.isoDateString(this.user.created_at),
      hasFirstNotification: this.indicatorMetrics.has_first_notification,
      mostRecentFullStorySession: this.currentFSSession,
      agentPermissions: this.indicatorMetrics.agent_permissions,
      visitorIdAccountId: this.agency ? `${id} - ${this.agency.id}` : null,
      appleLogin: this.indicatorMetrics.apple_login,
    };
    return userData;
  }

  private get currentFSSession(): string {
    return fsSession(false);
  }

  private get whitelistedProps(): string[] {
    return [
      'id',
      'created_at',
      'hipaa',
      'role',
      'active_tracker_count',
      'state',
      'mcc',
      'calls_enabled',
      'forms_enabled',
      'lead_with_lead_center',
      'active_company_count',
    ];
  }

  private get platforms(): ProductType[] {
    return this.agency.rate_plans?.map((plan) => plan.platform) || [];
  }

  private getPlanName(platform: string) {
    return this.agency.rate_plans
      ?.filter((plan) => plan.platform === platform)
      ?.map((plan) => plan.name)?.[0];
  }

  private hipaaSensitiveValue(contextObject, properties: string[]) {
    if (!this.user.hipaa) {
      return properties.map((propertyName) => {
        return contextObject[propertyName];
      });
    }
    return properties.map((propertyName) => {
      return this.whitelistedProps.includes(propertyName)
        ? contextObject[propertyName]
        : null;
    });
  }

  private isoDateString(date: string): string {
    return dayjs(date).toISOString();
  }
}
